// src/components/AppShell.js
import React, { useEffect, useCallback, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { BarChart3, Ticket, BookOpen, Inbox, Settings, Bell, LogOut, User, Camera, ChevronDown, Minimize2, Moon, Sun, Trash2, X, CheckCircle2, Shield, Calendar, Clock, Check, Globe, Menu } from 'lucide-react';
import logoSidebar from '../assets/beo-logo-final-trimmed.png';
import { timeAgo } from '../utils/formatters';
import { apiFetch, API_BASE } from '../utils/api';

/* ── Profile Dropdown Component ───────────────────────── */
function ProfileDropdown({ user, initials, onLogout, onClose, onOpenProfile, onOpenNotifications, darkMode, onToggleDarkMode, onRefreshUser }) {
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);
  const [compactMode, setCompactMode] = useState(() => localStorage.getItem('beo_compact_mode') === 'true');

  const profilePhoto = user?.avatar_url ? (user.avatar_url.startsWith('http') ? user.avatar_url : `${API_BASE}${user.avatar_url}?t=${user.updated_at || Date.now()}`) : null;

  const displayName = (() => {
    const n = user?.display_name || user?.username || 'User';
    return n.charAt(0).toUpperCase() + n.slice(1);
  })();

  const userEmail = user?.email || (user?.username ? `${user.username}@beo.in` : 'user@beo.in');
  const roleBadge = user?.role === 'admin' ? 'Admin' : user?.role === 'it' ? 'IT Support' : 'Employee';
  const roleBadgeColor = user?.role === 'admin' ? 'var(--text-60a5fa)' : user?.role === 'it' ? 'var(--text-34d399)' : '#a78bfa';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Photo must be under 2MB');
      return;
    }

    const formData = new FormData();
    formData.append('photo', file);

    const token = localStorage.getItem('beo_token');
    const uploadUrl = `${API_BASE}/api/users/profile-photo`;

    try {
      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) {
        let errMsg = `Upload failed (${res.status})`;
        try {
          const data = await res.json();
          if (data.error) errMsg = `Upload failed: ${data.error}`;
        } catch (e) { }
        throw new Error(errMsg);
      }

      const data = await res.json();
      if (onRefreshUser && data.token) onRefreshUser(data.token);
    } catch (err) {
      alert(err.message || 'Failed to upload photo');
    } finally {
      e.target.value = '';
    }
  };

  const handleRemovePhoto = async (e) => {
    e.stopPropagation();
    const token = localStorage.getItem('beo_token');
    const deleteUrl = `${API_BASE}/api/users/profile-photo`;
    try {
      const res = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (onRefreshUser && data.token) onRefreshUser(data.token);
    } catch (err) {
      alert('Failed to remove photo');
    }
  };

  const toggleCompactMode = () => {
    const next = !compactMode;
    setCompactMode(next);
    localStorage.setItem('beo_compact_mode', next.toString());
    document.documentElement.classList.toggle('compact-mode', next);
  };

  return (
    <div
      ref={dropdownRef}
      style={{
        position: 'absolute',
        top: 'calc(100% + 8px)',
        right: 0,
        width: 280,
        background: 'var(--surface-3)',
        border: '1px solid rgba(148, 163, 184, 0.1)',
        borderRadius: 16,
        boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(148, 163, 184, 0.05)',
        zIndex: 999,
        overflow: 'hidden',
        animation: 'profileDropIn 0.2s ease-out',
      }}
    >
      {/* ── Profile Header ── */}
      <div style={{
        padding: '20px 20px 16px',
        borderBottom: '1px solid rgba(148, 163, 184, 0.06)',
        display: 'flex',
        gap: 14,
        alignItems: 'center',
      }}>
        {/* Avatar with upload */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: profilePhoto ? 'transparent' : 'rgba(96, 165, 250, 0.1)',
              border: `2px solid ${roleBadgeColor}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              cursor: 'pointer',
            }}
            onClick={() => fileInputRef.current?.click()}
            title="Change profile photo"
          >
            {profilePhoto ? (
              <img src={profilePhoto} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--brand)' }}>{initials}</span>
            )}
          </div>
          <div
            onClick={() => fileInputRef.current?.click()}
            style={{
              position: 'absolute',
              bottom: -2,
              right: -2,
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: 'var(--surface-3)',
              border: '2px solid rgba(148, 163, 184, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <Camera size={10} color="#a8b3c4" />
          </div>
          {profilePhoto && (
            <div
              onClick={handleRemovePhoto}
              title="Remove photo"
              style={{
                position: 'absolute',
                top: -2,
                right: -2,
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: '#ef4444',
                border: '2px solid rgba(148, 163, 184, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <Trash2 size={9} color="#fff" />
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handlePhotoUpload}
          />
        </div>

        {/* Name / Email / Role */}
        <div style={{ overflow: 'hidden', flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayName}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {userEmail}
          </div>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            marginTop: 6,
            background: `${roleBadgeColor}15`,
            border: `1px solid ${roleBadgeColor}30`,
            padding: '2px 8px',
            borderRadius: 20,
            fontSize: 10,
            fontWeight: 800,
            color: roleBadgeColor,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}>
            {roleBadge}
          </div>
        </div>
      </div>

      {/* ── Menu Items ── */}
      <div style={{ padding: '6px 8px' }}>
        <ProfileMenuItem icon={<User size={15} />} label="My Profile" onClick={() => { onClose(); onOpenProfile(); }} />
        <ProfileMenuItem icon={<Bell size={15} />} label="Notifications" onClick={() => { onClose(); onOpenNotifications(); }} />
      </div>

      {/* ── Theme Controls ── */}
      <div style={{ padding: '4px 8px', borderTop: '1px solid rgba(148, 163, 184, 0.06)' }}>
        <div style={{ padding: '4px 12px', marginBottom: 2 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Preferences
          </span>
        </div>
        <ProfileToggleItem
          icon={<Moon size={15} />}
          label="Dark Mode"
          checked={darkMode}
          onChange={onToggleDarkMode}
        />
        <ProfileToggleItem
          icon={<Minimize2 size={15} />}
          label="Compact Mode"
          checked={compactMode}
          onChange={toggleCompactMode}
        />
      </div>

      {/* ── Logout ── */}
      <div style={{ padding: '4px 8px 8px', borderTop: '1px solid rgba(148, 163, 184, 0.06)' }}>
        <ProfileMenuItem
          icon={<LogOut size={15} />}
          label="Logout"
          danger
          onClick={onLogout}
        />
      </div>
    </div>
  );
}

function ProfileMenuItem({ icon, label, onClick, danger }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '9px 12px',
        borderRadius: 8,
        border: 'none',
        background: hovered ? 'rgba(148, 163, 184, 0.06)' : 'transparent',
        color: danger ? '#f87171' : (hovered ? 'var(--text-primary)' : 'var(--text-secondary)'),
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        textAlign: 'left',
      }}
    >
      {icon}
      {label}
    </button>
  );
}

function ProfileToggleItem({ icon, label, checked, onChange, disabled }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={!disabled ? onChange : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '9px 12px',
        borderRadius: 8,
        background: hovered && !disabled ? 'rgba(148, 163, 184, 0.06)' : 'transparent',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'all 0.15s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600 }}>
        {icon}
        {label}
      </div>
      {/* Toggle switch */}
      <div style={{
        width: 32,
        height: 18,
        borderRadius: 10,
        background: checked ? 'var(--brand)' : 'rgba(148, 163, 184, 0.15)',
        position: 'relative',
        transition: 'background 0.2s ease',
        opacity: disabled ? 0.5 : 1,
      }}>
        <div style={{
          position: 'absolute',
          top: 2,
          left: checked ? 16 : 2,
          width: 14,
          height: 14,
          borderRadius: '50%',
          background: '#fff',
          transition: 'left 0.2s ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        }} />
      </div>
    </div>
  );
}

/* ── Notifications Dropdown Component ─────────────────── */
function NotificationsDropdown({ user, notifications, onMarkAsRead, onMarkAllRead, onClose }) {
  const dropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div
      ref={dropdownRef}
      style={{
        position: 'absolute',
        top: 'calc(100% + 12px)',
        right: -10,
        width: 380,
        maxHeight: 460,
        background: 'var(--surface-3)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        borderRadius: 20,
        boxShadow: '0 25px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255, 255, 255, 0.05)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        animation: 'notifPanelScaleIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
        backdropFilter: 'blur(24px)',
        overflow: 'hidden'
      }}
    >
      <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255, 255, 255, 0.02)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>Notifications</h3>
          {notifications.filter(n => !n.read).length > 0 && (
            <span style={{ background: 'rgba(96, 165, 250, 0.15)', color: 'var(--text-60a5fa)', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, border: '1px solid rgba(96, 165, 250, 0.3)' }}>
              {notifications.filter(n => !n.read).length} new
            </span>
          )}
        </div>
        {notifications.some(n => !n.read) && (
          <button onClick={onMarkAllRead} style={{ background: 'transparent', border: 'none', color: 'var(--text-60a5fa)', fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s ease', padding: '4px 8px', borderRadius: 6 }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(96, 165, 250, 0.1)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            Mark all read
          </button>
        )}
      </div>
      <div style={{ overflowY: 'auto', flex: 1, padding: '10px 0', scrollBehavior: 'smooth' }}>
        {notifications.length === 0 ? (
          <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(255, 255, 255, 0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
              <Bell size={26} style={{ color: 'var(--white-40)' }} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>All caught up!</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 220, lineHeight: 1.4 }}>When you get new updates on your tickets, they'll show up here.</div>
            </div>
          </div>
        ) : (
          notifications.map(notif => (
            <div
              key={notif.id}
              onClick={() => onMarkAsRead(notif.id)}
              style={{
                padding: '16px 24px',
                display: 'flex',
                gap: 16,
                cursor: 'pointer',
                background: notif.read ? 'transparent' : 'rgba(96, 165, 250, 0.06)',
                borderLeft: `4px solid ${notif.read ? 'transparent' : 'var(--text-60a5fa)'}`,
                transition: 'all 0.2s ease',
                opacity: notif.read ? 0.7 : 1,
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = notif.read ? 'rgba(255, 255, 255, 0.03)' : 'rgba(96, 165, 250, 0.1)';
                e.currentTarget.style.opacity = '1';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = notif.read ? 'transparent' : 'rgba(96, 165, 250, 0.06)';
                e.currentTarget.style.opacity = notif.read ? '0.7' : '1';
              }}
            >
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: notif.read ? 'rgba(255, 255, 255, 0.05)' : 'rgba(96, 165, 250, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: `1px solid ${notif.read ? 'rgba(255,255,255,0.08)' : 'rgba(96, 165, 250, 0.3)'}` }}>
                {notif.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, color: notif.read ? 'var(--text-secondary)' : 'var(--text-primary)', fontWeight: notif.read ? 500 : 700, lineHeight: 1.4, marginBottom: 4 }}>
                  {notif.message}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>
                  <span>{notif.time}</span>
                  {notif.ticket_title && (
                    <>
                      <span>•</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{notif.ticket_title}</span>
                    </>
                  )}
                </div>
              </div>
              {!notif.read && (
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--text-60a5fa)', marginTop: 6, flexShrink: 0, boxShadow: '0 0 10px rgba(96, 165, 250, 0.6)' }} />
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ── Profile Modal Component ───────────────────────────── */
function ProfileModal({ user, initials, profilePhoto, onClose, darkMode }) {
  const userEmail = user?.email || (user?.username ? `${user.username}@beo.in` : 'user@beo.in');
  const roleBadge = user?.role === 'admin' ? 'Admin' : user?.role === 'it' ? 'IT Support' : 'Employee';

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)',
      zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20
    }}>
      <div style={{
        background: 'var(--surface)', width: '100%', maxWidth: 500,
        borderRadius: 16, border: '1px solid var(--border)',
        boxShadow: '0 25px 50px -12px var(--black-50)',
        overflow: 'hidden', animation: 'profileDropIn 0.2s ease-out'
      }}>
        {/* Header with background gradient */}
        <div style={{ height: 100, background: 'var(--brand-gradient)', position: 'relative' }}>
          <button onClick={onClose} style={{
            position: 'absolute', top: 16, right: 16, background: 'var(--black-20)',
            border: 'none', color: '#fff', width: 32, height: 32, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
          }}>
            <X size={18} />
          </button>
        </div>

        {/* Profile Content */}
        <div style={{ padding: '0 24px 24px', position: 'relative' }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%',
            background: 'var(--surface)', border: '4px solid var(--surface)',
            marginTop: -40, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 28, fontWeight: 800, color: 'var(--brand)', overflow: 'hidden'
          }}>
            {profilePhoto ? (
              <img src={profilePhoto} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : initials}
          </div>

          <h2 style={{ margin: '0 0 4px', fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>
            {user?.display_name || user?.username || 'User'}
          </h2>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 16 }}>{userEmail}</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 24 }}>
            <div style={{ background: 'var(--surface-3)', padding: 16, borderRadius: 12, border: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', marginBottom: 8, fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>
                <Shield size={14} /> Role
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{roleBadge}</div>
            </div>
            <div style={{ background: 'var(--surface-3)', padding: 16, borderRadius: 12, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', marginBottom: 8, fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>
                <Globe size={14} /> Time Zone
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }} title={user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'}>
                {user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'System Default'}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 24, padding: 16, background: 'var(--surface-3)', borderRadius: 12, border: '1px solid var(--border-subtle)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>System Information</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Theme Preference</span>
              <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 500 }}>{darkMode ? 'Dark' : 'Light'} Mode</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Account Type</span>
              <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 500 }}>Active Directory / LDAP</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Status</span>
              <span style={{ color: '#10b981', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Check size={12} /> Active
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Main AppShell ─────────────────────────────────────── */
export default function AppShell({ children, user, onLogout, currentPath, onRefreshUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);
  const getInitialDarkMode = () => {
    localStorage.removeItem('beo_dark_mode');
    if (!user || !user.username) return true;
    const stored = localStorage.getItem(`beo_theme_${user.username}`);
    if (stored) return stored === 'dark';
    if (user.theme_preference) return user.theme_preference === 'dark';
    return true;
  };

  const [darkMode, setDarkMode] = useState(getInitialDarkMode);
  const [notifications, setNotifications] = useState([]);
  const idleTimer = useRef(null);
  const warningTimer = useRef(null);

  useEffect(() => {
    if (user && user.username) {
      const stored = localStorage.getItem(`beo_theme_${user.username}`);
      const isDark = stored ? stored === 'dark' : (user.theme_preference ? user.theme_preference === 'dark' : true);
      setDarkMode(isDark);
    } else {
      setDarkMode(true);
    }
  }, [user]);

  useEffect(() => {
    document.documentElement.classList.toggle('light-mode', !darkMode);
  }, [darkMode]);

  const toggleDarkMode = async () => {
    const next = !darkMode;
    setDarkMode(next);
    if (user && user.username) {
      const themeStr = next ? 'dark' : 'light';
      localStorage.setItem(`beo_theme_${user.username}`, themeStr);
      try {
        const token = localStorage.getItem('beo_token');
        const res = await fetch('/api/users/theme', {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ theme: themeStr })
        });
        const data = await res.json();
        if (data.token && onRefreshUser) {
          onRefreshUser(data.token);
        }
      } catch (err) {
        console.error('Failed to save theme preference:', err);
      }
    }
  };

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem('beo_token');
      if (!token) return;
      const res = await fetch('/api/dashboard/recent-updates', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();

      const mapped = data.map(item => {
        let icon = <Clock size={14} color="#f59e0b" />;
        const m = (item.message || '').toLowerCase();
        if (item.source === 'comment') icon = <User size={14} color="var(--text-60a5fa)" />;
        else if (m.includes('resolved') || m.includes('closed')) icon = <CheckCircle2 size={14} color="#10b981" />;
        else if (m.includes('new ticket')) icon = <Ticket size={14} color="#ef4444" />;

        return {
          id: item.id || `${item.source}-${item.ticket_id}-${item.created_at}`,
          message: item.message,
          ticket_title: item.ticket_title,
          time: timeAgo(item.created_at),
          read: !!item.read,
          icon
        };
      });
      setNotifications(mapped);
    } catch (err) {
      console.error('Notifications Error:', err);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    const timer = setInterval(fetchNotifications, 60000);
    return () => clearInterval(timer);
  }, [fetchNotifications]);

  const handleMarkAsRead = async (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    try {
      const token = localStorage.getItem('beo_token');
      await fetch(`/api/notifications/${id}/read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) { console.error('Mark read failed:', err); }
  };

  const handleMarkAllRead = async () => {
    const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    if (unreadIds.length === 0) return;
    try {
      const token = localStorage.getItem('beo_token');
      await fetch('/api/notifications/mark-all-read', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ids: unreadIds })
      });
    } catch (err) { console.error('Mark all read failed:', err); }
  };

  // Initialize compact mode from localStorage on mount
  useEffect(() => {
    const isCompact = localStorage.getItem('beo_compact_mode') === 'true';
    document.documentElement.classList.toggle('compact-mode', isCompact);
  }, []);

  const performLogout = useCallback(() => {
    onLogout();
  }, [onLogout]);

  const resetIdleTimer = useCallback(() => {
    if (showWarning) setShowWarning(false);

    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);

    // Show warning after 29 minutes
    warningTimer.current = setTimeout(() => {
      setShowWarning(true);
    }, 29 * 60 * 1000);

    // Logout after 30 minutes
    idleTimer.current = setTimeout(() => {
      performLogout();
    }, 30 * 60 * 1000);
  }, [performLogout, showWarning]);

  useEffect(() => {
    // Absolute session limit check (12 hours)
    if (user && user.iat) {
      const now = Math.floor(Date.now() / 1000);
      const sessionAge = now - user.iat;
      const twelveHours = 12 * 60 * 60;

      if (sessionAge > twelveHours) {
        performLogout();
        return;
      }

      const timeLeft = twelveHours - sessionAge;
      const absoluteTimer = setTimeout(() => {
        performLogout();
      }, timeLeft * 1000);

      return () => clearTimeout(absoluteTimer);
    }
  }, [user, performLogout]);

  useEffect(() => {
    // Activity listeners
    const events = ['mousemove', 'keypress', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetIdleTimer));

    resetIdleTimer(); // Initial start

    return () => {
      events.forEach(e => window.removeEventListener(e, resetIdleTimer));
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (warningTimer.current) clearTimeout(warningTimer.current);
    };
  }, [resetIdleTimer]);

  const links = [
    { name: 'Dashboard', path: '/dashboard', icon: <BarChart3 size={20} strokeWidth={1.5} /> },
    { name: 'Tickets', path: '/tickets', icon: <Ticket size={20} strokeWidth={1.5} /> },
    { name: 'Knowledge Base', path: '/knowledge-base', icon: <BookOpen size={20} strokeWidth={1.5} /> },
  ];


  if (user && ['admin', 'it'].includes(user.role)) {
    links.push({ name: 'My Queue', path: '/my-queue', icon: <Inbox size={20} strokeWidth={1.5} /> });
    links.push({ name: 'Admin', path: '/admin', icon: <Settings size={20} strokeWidth={1.5} /> });
  }

  const initials = (user?.username || 'U').substring(0, 2).toUpperCase();
  const profilePhoto = user?.avatar_url ? (user.avatar_url.startsWith('http') ? user.avatar_url : `${API_BASE}${user.avatar_url}?t=${user.updated_at || Date.now()}`) : null;

  return (
    <div className="layout bg-page-gradient">
      {showWarning && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(239, 68, 68, 0.95)',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          zIndex: 9999,
          boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.2)',
          animation: 'slideDown 0.3s ease-out'
        }}>
          <div style={{ fontWeight: 'bold' }}>Session Security:</div>
          <div>You will be logged out in 1 minute due to inactivity. Move your mouse to stay logged in.</div>
          <button
            onClick={() => resetIdleTimer()}
            style={{
              background: 'white',
              color: '#ef4444',
              border: 'none',
              padding: '4px 12px',
              borderRadius: '4px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            I'm still here
          </button>
        </div>
      )}
      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div 
          onClick={() => setMobileMenuOpen(false)} 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 999,
            backdropFilter: 'blur(4px)'
          }}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${mobileMenuOpen ? 'mobile-visible' : ''}`} style={{ backdropFilter: 'blur(10px)' }}>
        <div className="sidebar-logo" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer', padding: '18px 12px 14px', height: 'auto', minHeight: '60px', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
          <img
            src={logoSidebar}
            alt="BEO IT Operations Helpdesk"
            style={{
              width: '100%',
              maxWidth: '220px',
              height: 'auto',
              objectFit: 'contain',
              display: 'block',
              margin: '0 auto'
            }}
          />
          {mobileMenuOpen && (
            <button 
              onClick={(e) => { e.stopPropagation(); setMobileMenuOpen(false); }}
              style={{
                position: 'absolute',
                right: 12,
                top: 18,
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                display: 'none'
              }}
              className="mobile-close-btn"
            >
              <X size={20} />
            </button>
          )}
        </div>

        <nav className="sidebar-nav">
          {links.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`nav-link ${currentPath === link.path ? 'active' : ''}`}
            >
              <span className="nav-link-icon">{link.icon}</span>
              <span className="nav-link-text">{link.name}</span>
            </Link>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <header className="topbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'transparent', borderBottom: '1px solid var(--border)', padding: '12px 24px', zIndex: 10 }}>
          <div className="topbar-left" style={{ display: 'flex', alignItems: 'center' }}>
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="mobile-menu-btn"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px',
                marginRight: '12px',
                borderRadius: '8px',
                border: '1px solid var(--border)'
              }}
            >
              <Menu size={20} />
            </button>
          </div>
          <div className="topbar-right" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: 'auto', position: 'relative' }}>
            {/* Notification Bell */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowNotificationsDropdown(!showNotificationsDropdown)}
                title="Notifications"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  border: '1px solid var(--border)',
                  background: showNotificationsDropdown ? 'rgba(148, 163, 184, 0.06)' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  position: 'relative',
                }}
                onMouseEnter={e => { if (!showNotificationsDropdown) e.currentTarget.style.background = 'rgba(148, 163, 184, 0.06)' }}
                onMouseLeave={e => { if (!showNotificationsDropdown) e.currentTarget.style.background = 'transparent' }}
              >
                <Bell size={16} color="var(--text-muted)" />
                {notifications.some(n => !n.read) && (
                  <div style={{ position: 'absolute', top: 6, right: 8, width: 8, height: 8, borderRadius: '50%', background: '#ef4444', border: '2px solid var(--surface)' }} />
                )}
              </button>

              {showNotificationsDropdown && (
                <NotificationsDropdown
                  user={user}
                  notifications={notifications}
                  onMarkAsRead={handleMarkAsRead}
                  onMarkAllRead={handleMarkAllRead}
                  onClose={() => setShowNotificationsDropdown(false)}
                />
              )}
            </div>

            {/* Avatar + Dropdown Trigger */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '4px 10px 4px 4px',
                  borderRadius: 24,
                  border: '1px solid var(--border)',
                  background: showProfileDropdown ? 'rgba(148, 163, 184, 0.06)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={e => { if (!showProfileDropdown) e.currentTarget.style.background = 'rgba(148, 163, 184, 0.04)'; }}
                onMouseLeave={e => { if (!showProfileDropdown) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: profilePhoto ? 'transparent' : 'rgba(96, 165, 250, 0.08)',
                  border: '1px solid rgba(96, 165, 250, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}>
                  {profilePhoto ? (
                    <img src={profilePhoto} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--brand)' }}>{initials}</span>
                  )}
                </div>
                <ChevronDown
                  size={14}
                  color="var(--text-muted)"
                  style={{
                    transform: showProfileDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </button>

              {/* Profile Dropdown Panel */}
              {showProfileDropdown && (
                <ProfileDropdown
                  user={user}
                  initials={initials}
                  onLogout={onLogout}
                  onClose={() => setShowProfileDropdown(false)}
                  onOpenProfile={() => setShowProfileModal(true)}
                  onOpenNotifications={() => setShowNotificationsDropdown(true)}
                  darkMode={darkMode}
                  onToggleDarkMode={toggleDarkMode}
                  onRefreshUser={onRefreshUser}
                />
              )}
            </div>
          </div>
        </header>

        <div className="content-area">{children}</div>
      </main>

      {/* Profile Modal */}
      {showProfileModal && (
        <ProfileModal
          user={user}
          initials={initials}
          profilePhoto={profilePhoto}
          onClose={() => setShowProfileModal(false)}
          darkMode={darkMode}
        />
      )}

      {/* Dropdown Animation */}
      <style>{`
        @keyframes profileDropIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes notifPanelScaleIn {
          from { opacity: 0; transform: translateY(-10px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        /* Compact Mode: reduce padding/margins slightly across the app */
        .compact-mode .content-area { padding: 16px 20px !important; }
        .compact-mode .card { padding: 16px !important; }
        .compact-mode .tdw-card { padding: 16px !important; margin-bottom: 14px !important; }
        .compact-mode .tdw-sidebar-card { padding: 14px !important; }
        .compact-mode .sidebar-nav .nav-link { padding: 8px 16px !important; }
        .compact-mode h1 { font-size: 1.3rem !important; }
        .compact-mode .form-group { margin-bottom: 16px !important; }

        @media (max-width: 768px) {
          .mobile-menu-btn {
            display: flex !important;
          }
          .mobile-close-btn {
            display: block !important;
          }
          .sidebar.mobile-visible {
            display: flex !important;
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            height: 100vh !important;
            width: 260px !important;
            z-index: 1000 !important;
            box-shadow: 5px 0 25px rgba(0,0,0,0.4) !important;
            animation: slideInLeft 0.25s ease-out !important;
          }
        }
        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
